import { AxiosError } from 'axios';

import { api } from '@/lib/api/axios';
import { ApiError, toApiError } from '@/lib/api/error';
import type {
  CreateMachineTypeBody,
  MachineTypeDetail,
  MachineTypeListItem,
  MachineTypesListQuery,
  SchemaChangeImpact,
  UpdateMachineTypeBody,
} from '@/types/machine-types';
import type { PaginatedResult } from '@/types/roles';

/**
 * Plan 10-02 D-95 — typed wrappers around the 6 `/api/machine-types` endpoints
 * defined in Plan 10-01.
 *
 * Each wrapper:
 *  - Throws an `ApiError` (via `toApiError`) on non-2xx so consumers can
 *    `instanceof`-switch on `e.status`.
 *  - Returns the typed response object on success.
 *
 * Permission gating is UI-only convenience (T-10-12). The backend re-checks
 * every permission via `[RequirePermission(...)]` (Plan 10-01).
 */

function buildListUrl(q: MachineTypesListQuery): string {
  const params = new URLSearchParams();
  if (q.q !== undefined && q.q !== '') params.set('q', q.q);
  if (q.status !== undefined) params.set('status', q.status);
  params.set('page', String(q.page ?? 1));
  params.set('pageSize', String(q.pageSize ?? 20));
  return `/api/machine-types?${params.toString()}`;
}

export async function listMachineTypesRequest(
  query: MachineTypesListQuery = {},
): Promise<PaginatedResult<MachineTypeListItem>> {
  try {
    const { data } = await api.get<PaginatedResult<MachineTypeListItem>>(buildListUrl(query));
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getMachineTypeRequest(id: number): Promise<MachineTypeDetail> {
  try {
    const { data } = await api.get<MachineTypeDetail>(`/api/machine-types/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function createMachineTypeRequest(
  body: CreateMachineTypeBody,
): Promise<MachineTypeDetail> {
  try {
    const { data } = await api.post<MachineTypeDetail>('/api/machine-types', body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** Specialized ApiError thrown by `updateMachineTypeRequest` when the backend
 * returns 400 with `requiresForce: true` plus the four impact arrays. The caller
 * (MachineTypeEditPage) reads `.impact` to render the SchemaChangeWarningDialog. */
export class SchemaChangeBlockedError extends ApiError {
  public readonly impact: SchemaChangeImpact;

  constructor(message: string, status: number, impact: SchemaChangeImpact, traceId?: string) {
    const opts: { traceId?: string } = traceId !== undefined ? { traceId } : {};
    super(message, status, opts);
    this.name = 'SchemaChangeBlockedError';
    this.impact = impact;
  }
}

/** Type-guard for the schema-change override-required error path. */
export function isSchemaChangeBlocked(e: unknown): e is SchemaChangeBlockedError {
  return e instanceof SchemaChangeBlockedError;
}

/** Specialized ApiError thrown by `deactivateMachineTypeRequest` when the
 * backend returns 400 with `requiresForce: true` (Phase 11 only — Phase 10
 * always succeeds because the Machine table does not exist yet). */
export class DeactivateMachineTypeBlockedError extends ApiError {
  public readonly affectedMachineCount: number;

  constructor(message: string, status: number, affectedMachineCount: number, traceId?: string) {
    const opts: { traceId?: string } = traceId !== undefined ? { traceId } : {};
    super(message, status, opts);
    this.name = 'DeactivateMachineTypeBlockedError';
    this.affectedMachineCount = affectedMachineCount;
  }
}

/** Type-guard for the deactivate override-required error path. */
export function isDeactivateMachineTypeBlocked(e: unknown): e is DeactivateMachineTypeBlockedError {
  return e instanceof DeactivateMachineTypeBlockedError;
}

/**
 * Update a machine type. `force=true` bypasses the schema-change-impact guard
 * (which checks for removedKeys / changedDataTypes / addedRequired).
 *
 * When `force` is `false`/`undefined` and the body would break existing
 * machines, the backend returns 400 with `extensions.requiresForce: true` plus
 * the four impact arrays. The wrapper inspects the body and rethrows a
 * `SchemaChangeBlockedError` carrying the structured impact — callers use
 * `isSchemaChangeBlocked(e)` to decide whether to surface the warning dialog.
 */
export async function updateMachineTypeRequest(
  id: number,
  body: UpdateMachineTypeBody,
  force?: boolean,
): Promise<MachineTypeDetail> {
  try {
    const url =
      force === true
        ? `/api/machine-types/${String(id)}?force=true`
        : `/api/machine-types/${String(id)}`;
    const { data } = await api.put<MachineTypeDetail>(url, body);
    return data;
  } catch (e) {
    if (e instanceof AxiosError && e.response?.status === 400) {
      const data = e.response.data as
        | {
            requiresForce?: boolean;
            removedKeys?: readonly string[];
            changedDataTypes?: readonly string[];
            addedRequired?: readonly string[];
            affectedMachineCount?: number;
            title?: string;
            traceId?: string;
          }
        | undefined;
      if (data?.requiresForce === true) {
        const impact: SchemaChangeImpact = {
          requiresForce: true,
          removedKeys: data.removedKeys ?? [],
          changedDataTypes: data.changedDataTypes ?? [],
          addedRequired: data.addedRequired ?? [],
          affectedMachineCount:
            typeof data.affectedMachineCount === 'number' ? data.affectedMachineCount : 0,
        };
        throw new SchemaChangeBlockedError(
          data.title ?? 'Validation Failed',
          400,
          impact,
          data.traceId,
        );
      }
    }
    throw toApiError(e);
  }
}

/**
 * Soft-deactivate a machine type. `force=true` bypasses the affected-machines
 * blocker (Phase 11 only — Phase 10 has no Machine table so the default path
 * always returns 204).
 */
export async function deactivateMachineTypeRequest(id: number, force = false): Promise<void> {
  try {
    const url = force
      ? `/api/machine-types/${String(id)}?force=true`
      : `/api/machine-types/${String(id)}`;
    await api.delete(url);
  } catch (e) {
    if (e instanceof AxiosError && e.response?.status === 400) {
      const data = e.response.data as
        | {
            requiresForce?: boolean;
            affectedMachineCount?: number;
            title?: string;
            traceId?: string;
          }
        | undefined;
      if (data?.requiresForce === true) {
        const count = typeof data.affectedMachineCount === 'number' ? data.affectedMachineCount : 0;
        throw new DeactivateMachineTypeBlockedError(
          data.title ?? 'Validation Failed',
          400,
          count,
          data.traceId,
        );
      }
    }
    throw toApiError(e);
  }
}

export async function reactivateMachineTypeRequest(id: number): Promise<void> {
  try {
    await api.post(`/api/machine-types/${String(id)}/reactivate`);
  } catch (e) {
    throw toApiError(e);
  }
}
